/**
 * StatusBar -- Shows elapsed time and punch details while clocked in.
 * SDD 7.4 -- Elapsed time display with live ticking, category/position/apparatus.
 */

import { CATEGORIES } from '@shared/categories';
import type { CategoryId } from '@shared/categories';
import { getPositionLabel } from '@shared/positions';
import type { PositionId } from '@shared/positions';

interface StatusBarProps {
  clockIn: string;
  elapsedSeconds: number;
  category: string;
  position: string | null;
  apparatus: string | null;
}

function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes.toString().padStart(hours > 0 ? 2 : 1, '0')}m`);
  parts.push(`${seconds.toString().padStart(2, '0')}s`);
  return parts.join(' ');
}

function formatClockInTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getCategoryLabel(id: string): string {
  return CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

function buildDetailString(category: string, position: string | null, apparatus: string | null): string {
  const parts = [getCategoryLabel(category as CategoryId)];
  if (position) parts.push(getPositionLabel(position as PositionId));
  if (apparatus) parts.push(apparatus);
  return parts.join(' · ');
}

export default function StatusBar({ clockIn, elapsedSeconds, category, position, apparatus }: StatusBarProps) {
  return (
    <div className="w-full p-4 bg-surface-raised rounded-xl border border-edge-default text-center">
      <div className="text-xs text-content-tertiary uppercase tracking-wider">
        On duty since {formatClockInTime(clockIn)}
      </div>
      <div className="text-4xl font-bold text-action-primary tabular-nums mt-1">{formatElapsed(elapsedSeconds)}</div>
      <div className="text-sm text-content-secondary mt-1.5">{buildDetailString(category, position, apparatus)}</div>
    </div>
  );
}

export { formatElapsed };
